/**
 * Tegner en bane på et Leaflet-kart:
 *  - Løype: start → rundingsmerker (i rekkefølge) → mål. Hvert legg bues litt ut
 *    til høyre for seilretningen, så fram- og tilbakelegg på samme strekning
 *    skiller seg, og retningspila (i buens topp) ikke havner oppå hverandre.
 *  - Start-/mållinje som stiplede linjer (grønn/rød).
 *  - Rundingsmerker nummerert (1, 2, …) med babord/styrbord (B/S). Et merke som
 *    rundes flere ganger vises ÉN gang med samlet etikett (f.eks. «1 B, 4 S»).
 *  - Start-/målmerker som fargede prikker med navn på hover.
 *
 * Brukes av både live-kart og reprise. `render` tømmer og tegner på nytt.
 */
import L from 'leaflet';
import type { CourseGeo } from './api.ts';

const START_COLOR = '#22c55e';
const FINISH_COLOR = '#ef4444';
const ROUNDING_COLOR = '#f59e0b';
const ROUTE_COLOR = '#38bdf8';
/**
 * Etiketten på rundingsmerkene står et stykke til høyre for merket (med en strek
 * tilbake, se styles.css), så rundingene rundt merket ikke skjules. Den ligger i en
 * egen kartrute under båtene (markerPane = 600) og over løypa (overlayPane = 400).
 */
const MARK_LABEL_OFFSET_PX = 28;
const MARK_LABEL_PANE = 'rcMarkLabels';

interface LL {
  lat: number;
  lon: number;
}

export interface CourseLayer {
  render: (geo: CourseGeo | undefined) => void;
  remove: () => void;
}

function mid(a: LL, b: LL): LL {
  return { lat: (a.lat + b.lat) / 2, lon: (a.lon + b.lon) / 2 };
}

/** Grov kompasskurs a→b (0 = nord, med klokka) — til rotasjon av retningspila. */
function bearingDeg(a: LL, b: LL): number {
  const dLon = (b.lon - a.lon) * Math.cos(((a.lat + b.lat) / 2) * (Math.PI / 180));
  const dLat = b.lat - a.lat;
  return (Math.atan2(dLon, dLat) * 180) / Math.PI;
}

function sameSpot(a: LL, b: LL): boolean {
  return Math.abs(a.lat - b.lat) < 1e-7 && Math.abs(a.lon - b.lon) < 1e-7;
}

function sideLabel(side: string | null): string {
  return side === 'port' ? 'B' : side === 'starboard' ? 'S' : '';
}

/** Merkenummeret foran navnet («5 Fjordbåen» → «5» + «Fjordbåen»), som klubbens merkeliste. */
function splitMarkName(name: string): { nr: string; navn: string } {
  const m = /^(\d+)\s+(.+)$/.exec(name.trim());
  return m ? { nr: m[1]!, navn: m[2]! } : { nr: '', navn: name.trim() };
}

/**
 * Løpet som liste i seilingsrekkefølge: «0 - Start», «5B - Fjordbåen», …, «0 - Mål».
 * Koden er merkenummeret + B/S; start og mål får start-/målmerkenes felles nummer.
 */
export function lopRader(geo: CourseGeo | undefined): { kode: string; navn: string }[] {
  if (!geo) return [];
  const rounding = geo.marks.filter((m) => m.purpose === 'rounding').sort((a, b) => a.seq - b.seq);
  if (rounding.length === 0 && !geo.startLine && !geo.finishLine) return [];
  const linjeNr = (purpose: 'start' | 'finish') => {
    const nr = new Set(geo.marks.filter((m) => m.purpose === purpose).map((m) => splitMarkName(m.name).nr));
    return nr.size === 1 ? [...nr][0]! : '';
  };
  return [
    { kode: linjeNr('start'), navn: 'Start' },
    ...rounding.map((m) => {
      const { nr, navn } = splitMarkName(m.name);
      return { kode: nr + sideLabel(m.side), navn };
    }),
    { kode: linjeNr('finish'), navn: 'Mål' },
  ];
}

const arrowIcon = (deg: number) =>
  L.divIcon({
    className: 'rc-route-arrow',
    html: `<div style="color:${ROUTE_COLOR};font-size:16px;line-height:1;text-shadow:0 0 2px #0008;transform:rotate(${deg}deg)">▲</div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

export function createCourseLayer(map: L.Map): CourseLayer {
  const group = L.layerGroup().addTo(map);
  if (!map.getPane(MARK_LABEL_PANE)) {
    const pane = map.createPane(MARK_LABEL_PANE);
    pane.style.zIndex = '550';
    pane.style.pointerEvents = 'none';
  }

  function render(geo: CourseGeo | undefined) {
    group.clearLayers();
    if (!geo) return;

    const rounding = geo.marks
      .filter((m) => m.purpose === 'rounding')
      .sort((a, b) => a.seq - b.seq);

    // --- Løype-punkter: start → rundingsmerker → mål (behold gjentatte merker) ---
    const startMid = geo.startLine ? mid(geo.startLine[0]!, geo.startLine[1]!) : null;
    const finishMid = geo.finishLine ? mid(geo.finishLine[0]!, geo.finishLine[1]!) : null;
    const raw: LL[] = [];
    if (startMid) raw.push(startMid);
    for (const m of rounding) raw.push({ lat: m.lat, lon: m.lon });
    if (finishMid) raw.push(finishMid);
    const route: LL[] = [];
    for (const p of raw) {
      if (route.length === 0 || !sameSpot(route[route.length - 1]!, p)) route.push(p);
    }

    // Lokal plan-skala (lengdegrad komprimeres med cos(lat)) for riktig vinkel på buen.
    const latRef = route.length ? route.reduce((s, p) => s + p.lat, 0) / route.length : 59;
    const kx = Math.cos((latRef * Math.PI) / 180) || 1;
    const toXY = (p: LL) => ({ x: p.lon * kx, y: p.lat });
    const toLL = (x: number, y: number): LL => ({ lat: y, lon: x / kx });

    for (let i = 1; i < route.length; i++) {
      const A = route[i - 1]!;
      const B = route[i]!;
      const a = toXY(A);
      const b = toXY(B);
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) continue;
      // Perpendikulær til høyre for seilretningen (snur fortegn når retningen snur,
      // så fram- og tilbakelegg buer til hver sin side).
      const px = dy / len;
      const py = -dx / len;
      const bow = Math.max(0.0001, Math.min(0.0006, len * 0.15));
      const cx = (a.x + b.x) / 2 + px * bow;
      const cy = (a.y + b.y) / 2 + py * bow;
      const samples = 14;
      const pts: [number, number][] = [];
      for (let t = 0; t <= samples; t++) {
        const u = t / samples;
        const qx = (1 - u) * (1 - u) * a.x + 2 * (1 - u) * u * cx + u * u * b.x;
        const qy = (1 - u) * (1 - u) * a.y + 2 * (1 - u) * u * cy + u * u * b.y;
        const ll = toLL(qx, qy);
        pts.push([ll.lat, ll.lon]);
      }
      L.polyline(pts, { color: ROUTE_COLOR, weight: 2, opacity: 0.85 }).addTo(group);
      // Retningspil i buens topp (t = 0.5). Tangenten der er parallell med A→B.
      const apexLL = toLL(0.25 * a.x + 0.5 * cx + 0.25 * b.x, 0.25 * a.y + 0.5 * cy + 0.25 * b.y);
      L.marker([apexLL.lat, apexLL.lon], { interactive: false, icon: arrowIcon(bearingDeg(A, B)) }).addTo(group);
    }

    // --- Start-/mållinje ---
    if (geo.startLine) {
      L.polyline(
        geo.startLine.map((p) => [p.lat, p.lon] as [number, number]),
        { color: START_COLOR, weight: 3, opacity: 0.9, dashArray: '6 6' },
      )
        .bindTooltip('Startlinje', { direction: 'center' })
        .addTo(group);
    }
    if (geo.finishLine) {
      L.polyline(
        geo.finishLine.map((p) => [p.lat, p.lon] as [number, number]),
        { color: FINISH_COLOR, weight: 3, opacity: 0.9, dashArray: '6 6' },
      )
        .bindTooltip('Mållinje', { direction: 'center' })
        .addTo(group);
    }

    // --- Start-/målmerker (farget prikk, navn på hover) ---
    for (const m of geo.marks.filter((x) => x.purpose === 'start' || x.purpose === 'finish')) {
      L.circleMarker([m.lat, m.lon], {
        radius: 6,
        color: '#ffffff',
        weight: 1.5,
        fillColor: m.purpose === 'start' ? START_COLOR : FINISH_COLOR,
        fillOpacity: m.online ? 0.95 : 0.4,
      })
        .bindTooltip(m.name, { direction: 'top' })
        .addTo(group);
    }

    // --- Rundingsmerker: slå sammen merker som brukes flere ganger ---
    interface MarkGroup {
      lat: number;
      lon: number;
      name: string;
      online: boolean;
      items: { n: number; side: string | null }[];
    }
    const groups = new Map<string, MarkGroup>();
    rounding.forEach((m, i) => {
      const g = groups.get(m.markId);
      if (g) g.items.push({ n: i + 1, side: m.side });
      else
        groups.set(m.markId, {
          lat: m.lat,
          lon: m.lon,
          name: m.name,
          online: m.online,
          items: [{ n: i + 1, side: m.side }],
        });
    });
    for (const g of groups.values()) {
      const label =
        g.items.length === 1
          ? `${g.items[0]!.n}. ${g.name}${g.items[0]!.side ? ' · ' + sideLabel(g.items[0]!.side) : ''}`
          : `${g.name} · ${g.items
              .map((it) => it.n + (it.side ? ' ' + sideLabel(it.side) : ''))
              .join(', ')}`;
      L.circleMarker([g.lat, g.lon], {
        radius: 7,
        color: '#ffffff',
        weight: 1.5,
        fillColor: ROUNDING_COLOR,
        fillOpacity: g.online ? 0.95 : 0.4,
      })
        .bindTooltip(label, {
          permanent: true,
          direction: 'right',
          offset: [MARK_LABEL_OFFSET_PX, 0],
          pane: MARK_LABEL_PANE,
          className: 'rc-mark-label',
        })
        .addTo(group);
    }
  }

  function remove() {
    group.remove();
  }

  return { render, remove };
}
